import { userStore } from "../store/userStore"
import { getExistingFcmToken } from "./webPush"

type RequestResult<T> =
    | { status: "success"; data: T; httpStatus?: number }
    | { status: "error"; error: { message: string }; httpStatus?: number }

type RequestData = <T>(opts: {
    type: "GET" | "POST" | "PUT" | "DELETE" | "PATCH"
    path: string
    sendData?: Record<string, any>
    disableError?: boolean
}) => Promise<RequestResult<T>>

/** Сообщение бэкенда при невалидном / отсутствующем device token */
export function isDeviceTokenError(message: string): boolean {
    return /переустанов/i.test(message)
}

function isRateLimited(res: RequestResult<unknown>): boolean {
    if (res.status !== "error") return false
    if (res.httpStatus === 429) return true
    return /too many attempts|слишком много/i.test(res.error.message)
}

/** Общий in-flight + cooldown: не долбить /devicetoken при 429 и параллельных вызовах */
let inFlight: Promise<string | null> | null = null
let cooldownUntil = 0

const COOLDOWN_MS = 30_000
const COOLDOWN_429_MS = 60_000

export async function fetchDeviceToken(
    requestData: RequestData,
    { force = false }: { force?: boolean } = {},
): Promise<string | null> {
    if (!force) {
        const existing = userStore.getState().deviceToken
        if (existing) return existing
    }

    if (!force && Date.now() < cooldownUntil) {
        return userStore.getState().deviceToken
    }

    if (inFlight) return inFlight

    inFlight = (async () => {
        try {
            // Если пуши уже разрешены в браузере — пробуем взять реальный FCM token.
            // Не запрашиваем разрешение автоматически, чтобы не раздражать пользователя.
            try {
                const fcm = await getExistingFcmToken()
                if (fcm) {
                    userStore.getState().setDeviceToken(fcm)
                    // Синхронизируем с бэком так же, как мобилка (POST /token).
                    requestData({
                        type: "POST",
                        path: "token",
                        sendData: { token: fcm },
                        disableError: true,
                    }).catch(() => {})
                    cooldownUntil = 0
                    return fcm
                }
            } catch {
                // ignore: fallback на API токен
            }

            const res = await requestData<{ token?: string }>({
                type: "GET",
                path: "devicetoken",
                disableError: true,
            })

            if (res.status === "success" && res.data?.token) {
                userStore.getState().setDeviceToken(res.data.token)
                cooldownUntil = 0
                return res.data.token
            }

            cooldownUntil = Date.now() + (isRateLimited(res) ? COOLDOWN_429_MS : COOLDOWN_MS)
            return null
        } catch {
            cooldownUntil = Date.now() + COOLDOWN_MS
            return null
        } finally {
            inFlight = null
        }
    })()

    return inFlight
}

export async function ensureDeviceToken(requestData: RequestData): Promise<string | null> {
    return fetchDeviceToken(requestData)
}

export async function refreshDeviceToken(requestData: RequestData): Promise<string | null> {
    return fetchDeviceToken(requestData, { force: true })
}

export function getDeviceToken(): string | null {
    return userStore.getState().deviceToken
}
